import { Box, Button, InputLabel, MenuItem, TextField } from "@mui/material";
import ModalBox from "components/Modal";
import { Spinner } from "components/shared/Spinner";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCompanyList, sendInvitation } from "store/adminPanelSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import CreateUserModal from "../CreateUserModal";
import type { IInvitationForm } from "../types";

export const textFieldStyles = {
    "&.MuiTextField-root": {
        margin: "auto",
        width: "350px",
    },
    ".MuiFormHelperText-root": {
        color: "rgb(211, 47, 47)",
    },
};
export const labelStyles = {
    color: "#737373",
    width: "100%",
    justifyContent: "space-between",
    fontSize: "12px",
    lineHeight: "18px",
    ".MuiTypography-root": {
        fontSize: "12px",
    },
    "&.MuiInputLabel-root": {
        fontFamily: "Poppins",
        fontStyle: "normal",
        width: "200px",
    },
};

interface CreateUserPanelProps {}

const CreateUser = (props: CreateUserPanelProps) => {
    const [t] = useTranslation();

    const defaultValues: IInvitationForm = {
        accountType: "publisher",
        email: "",
        companyId: 1,
        name: "",
        surname: "",
        role: ROLES.OWNER,
    };

    const dispatch = useAppDispatch();
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<IInvitationForm>({
        defaultValues,
    });

    const [userData, setUserData] = useState<Record<string, any>>({});

    const { data: newUser, isLoading, error } = useAppSelector((state) => state.adminPanel.lastAddedUser);
    const {
        data: companyList,
        isLoading: isLoadingCompanies,
        error: errorCompanies,
    } = useAppSelector((state) => state.adminPanel.companies);

    const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, "gm");

    const onSubmit = (data: IInvitationForm) => {
        setUserData(data);
    };

    const validateModal = () => {
        dispatch(sendInvitation(userData as IInvitationForm)).then((response) => {
            response?.payload && reset(defaultValues);
        });
        setUserData({});
    };

    useEffect(() => {
        dispatch(getCompanyList());
    }, [dispatch]);

    return (
        <>
            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Spinner />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "1rem",
                    }}
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name="accountType"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="accountType">
                                    {t("AdminPanel.AccountType")}
                                </InputLabel>
                                <TextField {...field} id="outlined-select-currency" select>
                                    <MenuItem value={"beneficiary"} disabled>
                                        Beneficiary
                                    </MenuItem>
                                    <MenuItem value={"publisher"}>Publisher</MenuItem>
                                </TextField>
                            </div>
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            pattern: emailRegex,
                        }}
                        render={({ field: { ref, ...rest } }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="email">
                                    {t("AdminPanel.CompanyEmail")}*
                                </InputLabel>
                                <StyledTextField
                                    helperText={errors.email ? errors.email.message : false}
                                    {...rest}
                                    inputRef={ref}
                                    sx={textFieldStyles}
                                    placeholder={t("AdminPanel.CompanyEmail")}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="name">
                                    {t("AdminPanel.Name")}*
                                </InputLabel>
                                <StyledTextField
                                    helperText={errors.name ? errors.name.message : false}
                                    {...rest}
                                    inputRef={ref}
                                    sx={textFieldStyles}
                                    placeholder={t("AdminPanel.Name")}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="surname"
                        control={control}
                        render={({ field: { ref, ...rest } }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="name">
                                    {t("AdminPanel.Surname")}*
                                </InputLabel>
                                <StyledTextField
                                    helperText={errors.name ? errors.name.message : false}
                                    {...rest}
                                    inputRef={ref}
                                    sx={textFieldStyles}
                                    placeholder={t("AdminPanel.Surname")}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                            <div className="input-group-form">
                                <InputLabel sx={labelStyles} htmlFor="companyId">
                                    {t("AdminPanel.NameCompany")}
                                </InputLabel>
                                <TextField {...field} select>
                                    {companyList.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        )}
                    />
                    <Button type="submit" variant="outlined" color="primary">
                        Send invitation
                    </Button>
                </Box>
            )}
            <ModalBox
                onClose={() => setUserData({})}
                open={Object.keys(userData).length > 0}
                mainTitle={"Add user to company"}
            >
                <CreateUserModal
                    handleSubmit={() => validateModal()}
                    userData={{
                        ...userData,
                        companyName: companyList.find((company) => company.id == userData.companyId)?.name ?? "",
                    }}
                />
            </ModalBox>
        </>
    );
};
export default CreateUser;
